import { useEffect, useMemo, useState } from 'react';
import { useTable } from 'spacetimedb/react';
import { create } from 'zustand';
import { tables } from '@vidmakr/bindings';
import { CheckCircle2, ExternalLink, Loader2, Pencil, Plus, Send, Trash2, Webhook, XCircle } from 'lucide-react';
import { Button, Card, ConfirmModal, Field, Input, Modal } from '@/components/ui';
import { api } from '@/lib/api';
import { reducers } from '@/lib/stdb';
import { attempt, errorMessage, toast } from '@/lib/toast';

/**
 * Integrations: admins add endpoints in Settings (any URL that takes a JSON POST per
 * file: Zapier, Make, n8n, scripts, other apps). Anyone can then send assets and
 * finished exports to them from the asset library and the exports list. The API
 * does the sending (the other side fetches each file from a signed link); this
 * file shows the progress.
 */

export function useIntegrations(orgId: string | undefined) {
  const [rows] = useTable(tables.integration);
  return useMemo(() => rows.filter((r) => r.orgId === orgId).sort((a, b) => a.name.localeCompare(b.name)), [rows, orgId]);
}

// ---------------------------------------------------------------------------
// Settings card
// ---------------------------------------------------------------------------

interface Form {
  name: string;
  url: string;
  secret: string;
  folder: string;
  authHeader: string;
  clearSecret: boolean;
}

const emptyForm: Form = { name: '', url: '', secret: '', folder: '', authHeader: 'Authorization', clearSecret: false };

export function Integrations({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const rows = useIntegrations(orgId);
  const [editing, setEditing] = useState<{ id: string | null; form: Form; hasSecret: boolean } | null>(null);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  return (
    <Card className="p-5" data-integrations>
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="font-semibold">Integrations</h2>
        {canManage ? (
          <Button size="sm" onClick={() => setEditing({ id: null, form: emptyForm, hasSecret: false })} data-add-integration>
            <Plus size={14} /> Add integration
          </Button>
        ) : null}
      </div>
      <p className="mb-4 text-xs text-faint">
        Send media and finished exports to other tools: any URL that takes a JSON POST per file, such as Zapier, Make, n8n, your own scripts or another app’s import
        address. Everyone here can send; admins add and change integrations.
      </p>
      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-muted">No integrations yet.</div>
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {rows.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-integration-row={c.id}>
              <Webhook size={16} className="text-muted" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{c.name}</div>
                <div className="truncate text-xs text-faint">
                  POST {c.baseUrl.replace(/^https?:\/\//, '')}
                  {c.folder ? ` · folder “${c.folder}”` : ''}
                  {c.authHeader ? ` · ${c.authHeader}: …${c.keyHint}` : ' · no secret'}
                </div>
              </div>
              {canManage ? (
                <div className="flex items-center gap-1">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Edit integration"
                    onClick={() =>
                      setEditing({
                        id: c.id,
                        hasSecret: !!c.authHeader,
                        form: { name: c.name, url: c.baseUrl, secret: '', folder: c.folder, authHeader: c.authHeader || 'Authorization', clearSecret: false },
                      })
                    }
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button size="icon-sm" variant="ghost" aria-label="Remove integration" onClick={() => setRemoving({ id: c.id, name: c.name })}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editing ? <IntegrationDialog orgId={orgId} id={editing.id} hasSecret={editing.hasSecret} initial={editing.form} onClose={() => setEditing(null)} /> : null}
      <ConfirmModal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.name ?? ''}?`}
        message="Nobody here can send to it any more. Files already sent stay where they went."
        confirmLabel="Remove"
        danger
        onConfirm={async () => {
          if (removing) await attempt(() => reducers().deleteIntegration({ id: removing.id }), 'Integration removed');
          setRemoving(null);
        }}
      />
    </Card>
  );
}

const SAMPLE = `{
  "event": "file",
  "source": "vidmakr",
  "sentAt": "2026-11-02T10:00:00.000Z",
  "organization": { "id": "…", "name": "…" },
  "sentBy": { "name": "…", "email": "…" },
  "folder": "Launch/Trailer",
  "file": {
    "id": "…", "type": "export",
    "name": "Trailer_2026-11-02_1000.mp4",
    "url": "https://…signed download link…",
    "urlExpiresAt": "2026-11-02T16:00:00.000Z",
    "sizeBytes": 48211337, "mime": "video/mp4"
  }
}`;

function IntegrationDialog({ orgId, id, hasSecret, initial, onClose }: { orgId: string; id: string | null; hasSecret: boolean; initial: Form; onClose: () => void }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const keeping = !!id && hasSecret && !f.clearSecret;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api<{ host: string }>(id ? `/api/integrations/orgs/${orgId}/${id}` : `/api/integrations/orgs/${orgId}`, {
        method: id ? 'PATCH' : 'POST',
        json: f,
      });
      toast.success(`Test sent to ${r.host}: it answered OK`);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={id ? 'Edit integration' : 'Add integration'}
      width={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!f.url} onClick={() => void save()} data-save-integration>
            Test and save
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label="Name" hint="Shown in the Send menu.">
          <Input value={f.name} onChange={set('name')} placeholder="e.g. Social media, Archive (n8n)" autoFocus />
        </Field>
        <Field label="Endpoint URL" hint="Gets one JSON POST per file.">
          <Input value={f.url} onChange={set('url')} placeholder="https://…" spellCheck={false} />
        </Field>
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3">
          <Field label="Secret header">
            <Input value={f.authHeader} onChange={set('authHeader')} placeholder="Authorization" spellCheck={false} />
          </Field>
          <Field label="Secret" hint={keeping ? 'Empty: keep the current one.' : 'Optional. Stored encrypted.'}>
            <Input
              value={f.secret}
              onChange={(e) => setF({ ...f, secret: e.target.value, clearSecret: false })}
              placeholder={keeping ? '••••••••' : 'e.g. Bearer abc123'}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        </div>
        {id && hasSecret ? (
          <label className="flex items-center gap-2 text-xs text-muted">
            <input type="checkbox" checked={f.clearSecret} onChange={(e) => setF({ ...f, clearSecret: e.target.checked, secret: '' })} /> Send without a secret
          </label>
        ) : null}
        <Field label="Folder" hint="Optional. Sent as “folder”.">
          <Input value={f.folder} onChange={set('folder')} placeholder="e.g. vidmakr" />
        </Field>
        <details className="rounded-lg border border-line bg-panel-2 px-3 py-2 text-xs">
          <summary className="cursor-pointer text-muted">What vidmakr sends</summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre text-[11px] leading-relaxed text-muted">{SAMPLE}</pre>
          <p className="mt-2 text-faint">
            Saving sends a test (<code>"event": "test"</code>, no file), which must answer 2xx. The download link works without signing in for 6 hours. Answer with JSON
            containing <code>openUrl</code> (or <code>url</code>) to show an “Open” link after sending.
          </p>
        </details>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

interface SendRequest {
  orgId: string;
  assetIds?: string[];
  jobIds?: string[];
  /** Suggested subfolder (e.g. the project's name). */
  folder?: string;
  /** For the dialog title: "3 files", "Trailer v2.mp4"… */
  label: string;
}
interface SendItem {
  name: string;
  status: 'waiting' | 'sending' | 'sent' | 'failed';
  error?: string;
  libraryUrl?: string;
}
interface Progress {
  sendId: string;
  connectionName: string;
  items: SendItem[];
  done: boolean;
  skipped: string[];
}
interface SendState {
  request: SendRequest | null;
  progress: Progress | null;
  /** The integration last sent to, chosen first next time. */
  lastId: string;
  open(r: SendRequest): void;
  close(): void;
}

const useSend = create<SendState>((set) => ({
  request: null,
  progress: null,
  lastId: '',
  open: (request) => set({ request, progress: null }),
  close: () => set({ request: null }),
}));

/** Opens the "Send to…" dialog for some assets or exports. */
export const openSendTo = (r: SendRequest) => useSend.getState().open(r);

async function poll(sendId: string) {
  for (let i = 0; i < 3600; i++) {
    await new Promise((r) => setTimeout(r, i < 10 ? 1000 : 2500));
    let s: { done: boolean; connectionName: string; items: SendItem[] };
    try {
      s = await api(`/api/integrations/sends/${sendId}`);
    } catch {
      continue;
    }
    const cur = useSend.getState().progress;
    if (cur?.sendId === sendId) useSend.setState({ progress: { ...cur, ...s } });
    if (s.done) {
      const ok = s.items.filter((x) => x.status === 'sent').length;
      const failed = s.items.length - ok;
      // Tell people who closed the dialog.
      if (!useSend.getState().request) {
        if (failed) toast.error(`${s.connectionName}: ${failed} of ${s.items.length} file${s.items.length === 1 ? '' : 's'} failed`);
        else toast.success(`Sent ${ok} file${ok === 1 ? '' : 's'} to ${s.connectionName}`);
      }
      return;
    }
  }
}

/** Mount once near the app root (inside the SpacetimeDB provider). */
export function SendHost() {
  const { request, progress, close, lastId } = useSend();
  const rows = useIntegrations(request?.orgId);
  const [connId, setConnId] = useState('');
  const [folder, setFolder] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!request) return;
    setConnId((id) => (rows.some((c) => c.id === lastId) ? lastId : rows.some((c) => c.id === id) ? id : (rows[0]?.id ?? '')));
    setFolder(request.folder ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);
  if (!request) return null;
  const conn = rows.find((c) => c.id === connId);
  const send = async () => {
    if (!conn) return;
    setBusy(true);
    try {
      const r = await api<{ sendId: string; count: number; skipped: string[] }>(`/api/integrations/${conn.id}/send`, {
        method: 'POST',
        json: { assetIds: request.assetIds, jobIds: request.jobIds, folder },
      });
      useSend.setState({ lastId: conn.id, progress: { sendId: r.sendId, connectionName: conn.name, items: [], done: false, skipped: r.skipped } });
      void poll(r.sendId);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const target = [conn?.folder, folder].filter(Boolean).join('/');
  return (
    <Modal
      open
      onClose={close}
      title={progress ? `Sending to ${progress.connectionName}` : `Send ${request.label} to…`}
      width={500}
      footer={
        progress ? (
          <Button variant="primary" onClick={close}>
            {progress.done ? 'Done' : 'Keep sending in the background'}
          </Button>
        ) : rows.length ? (
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" loading={busy} disabled={!conn} onClick={() => void send()} data-send>
              <Send size={14} /> Send
            </Button>
          </>
        ) : (
          <Button onClick={close}>Close</Button>
        )
      }
    >
      {!progress ? (
        rows.length === 0 ? (
          <p className="text-sm text-muted">There’s nowhere to send to yet. An admin can add an integration under Settings → Integrations.</p>
        ) : (
          <div className="grid gap-3">
            {rows.length > 1 ? (
              <div className="grid gap-1.5" role="radiogroup" aria-label="Send to">
                {rows.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={c.id === connId}
                    onClick={() => setConnId(c.id)}
                    data-send-target={c.id}
                    className={
                      'flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors ' +
                      (c.id === connId ? 'border-accent bg-accent/10' : 'border-line bg-panel-2 hover:border-line-2')
                    }
                  >
                    <Webhook size={15} className="text-muted" />
                    <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                    <span className="truncate text-xs text-faint">{c.remoteOrgName}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="flex items-center gap-2 text-sm">
                <Webhook size={15} className="text-muted" /> To <b>{conn?.name}</b> <span className="text-faint">({conn?.remoteOrgName})</span>
              </p>
            )}
            <Field
              label="Subfolder"
              hint={target ? `Sent as folder “${target}”.` : 'Optional. Sent as “folder”.'}
            >
              <Input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="e.g. the project name" />
            </Field>
          </div>
        )
      ) : (
        <div className="grid gap-2" data-send-progress>
          {progress.items.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted">
              <Loader2 size={14} className="animate-spin" /> Starting…
            </div>
          ) : (
            <ul className="divide-y divide-line rounded-lg border border-line">
              {progress.items.map((it, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2 text-sm">
                  {it.status === 'sent' ? (
                    <CheckCircle2 size={15} className="shrink-0 text-ok" />
                  ) : it.status === 'failed' ? (
                    <XCircle size={15} className="shrink-0 text-danger" />
                  ) : (
                    <Loader2 size={15} className={it.status === 'sending' ? 'shrink-0 animate-spin text-accent-2' : 'shrink-0 text-faint'} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{it.name}</div>
                    {it.error ? <div className="truncate text-xs text-danger" title={it.error}>{it.error}</div> : null}
                  </div>
                  {it.libraryUrl ? (
                    <a className="inline-flex shrink-0 items-center gap-1 text-xs text-accent-2 hover:underline" href={it.libraryUrl} target="_blank" rel="noreferrer">
                      Open <ExternalLink size={12} />
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {progress.skipped.length ? <p className="text-xs text-faint">Skipped (still processing or only on a network folder): {progress.skipped.join(', ')}</p> : null}
          {!progress.done ? (
            <p className="text-xs text-faint">
              One request per file; big videos can take a few minutes. You can close this window.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

/** "Send to…" for a finished export (a conversion sends its new media file). */
export function sendExport(job: { id: string; orgId: string; kind: string; assetId: string; title: string }, place?: string) {
  const folder = (place ?? '').split('/').pop()?.trim() ?? '';
  if (job.kind === 'convert') openSendTo({ orgId: job.orgId, assetIds: [job.assetId], folder, label: `“${job.title}”` });
  else openSendTo({ orgId: job.orgId, jobIds: [job.id], folder, label: `“${job.title}”` });
}
